/**
 * DASHBOARD-RPS-TEAMS.WIDGET
 * Repartition of users for a survey
 */
import { withTranslation, WithTranslation } from "react-i18next"
import { useOpenHelpSection } from "@/utils/use-open-help-section.utils"
import { connect } from "react-redux"
import Card from "@/components/card"
import { useEffect, useState } from "react"
import { DashboardFilters } from "@/redux/filter.types"
import { store } from "@/index"
import { INSUFFICIENT_PARTICIPATION, STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import { fetchRpsOverview } from "@/redux/_rps.actions"
import NoResultsWidget from "./no-results.widget"
import { SurveyRps, SurveyState } from "@/redux/survey.types"
import { surveyEdit } from "@/redux/survey.actions"
import DashboardNumber from "@/components/dashboard-number"
import { faUserCircle } from "@fortawesome/free-solid-svg-icons"
import QuestionSpecialHelp from "@/help/question-special.help"
import RpsHelp from "@/help/rps.help"
import { loadParticipation } from "@/redux/_archive.actions"
import { useCountUpList } from "@/utils/use-count-up.utils"
import DashboardRpsOverviewSkeletonWidget from "./skeletons/dashboard-rps-overview-skeleton.widget"

interface StateProps {
  survey: SurveyState
}

interface OwnProps {
  dashboardFilters: DashboardFilters
  onLoadingChange?: (isLoading: boolean) => void //Pour que la page puisse afficher le skeleton du bas pendant le chargement
  isReport?: boolean
}

type Props = StateProps & OwnProps & WithTranslation

function DashboardRpsOverviewWidget(props: Props) {
  const { t } = props

  //Display help
  const [currentHelp, setCurrentHelp] = useState<string | null>(null)

  //The categories are described in the guide of the page : its info icons open it on the table of the categories, the old help is the fallback
  const openHelpSection = useOpenHelpSection()
  const openCategory = (name: string) => openHelpSection("categories", () => setCurrentHelp(name))

  //Legend for tag
  const [currentTag, setCurrentTag] = useState<string | null>(null)

  //Status of loading
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    props.onLoadingChange?.(isLoading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  //survey.active.rps est un état Redux global qui persiste d'une visite à l'autre de la
  //page : si on revient sur /dashboard/rps sans changer de compte, les valeurs sont déjà
  //en mémoire au montage, donc animer directement depuis ces props ferait tourner le
  //count-up DERRIÈRE le skeleton (souvent plus rapide que le vrai fetch) — plus rien à
  //animer une fois le skeleton retiré. On mirrore donc les valeurs dans un state local qui
  //repart toujours de 0 au montage, mis à jour seulement une fois CE chargement terminé.
  const [rpsDisplay, setRpsDisplay] = useState({ isoStrain: 0, jobStrain: 0, actives: 0, passives: 0, relaxed: 0 })

  const notConcerned =
    props.survey.active.participationSend -
    (rpsDisplay.actives + rpsDisplay.passives + rpsDisplay.relaxed) -
    (rpsDisplay.isoStrain + rpsDisplay.jobStrain)

  //La card reste sur son skeleton tant que la participation n'est pas chargée non plus : les compteurs ne
  //démarrent qu'à l'apparition de la card, sinon ils auraient fini derrière le skeleton
  const isCardLoading = isLoading || props.survey.status === STATUS_LOADING

  const [animatedIsoStrain, animatedJobStrain, animatedActives, animatedPassives, animatedRelaxed, animatedNc] =
    useCountUpList(
      isCardLoading
        ? [0, 0, 0, 0, 0, 0]
        : [
            rpsDisplay.isoStrain,
            rpsDisplay.jobStrain,
            rpsDisplay.actives,
            rpsDisplay.passives,
            rpsDisplay.relaxed,
            notConcerned
          ]
    )

  //Load data on every change on filters
  useEffect(() => {
    async function load() {
      if (!props.survey.active.randomData) {
        //Load data from server
        setIsLoading(true)
        const data: any = await store.dispatch(
          fetchRpsOverview(props.dashboardFilters, undefined, undefined, props.isReport)
        )

        if (data.error || data.message === "no_data") {
          store.dispatch(surveyEdit("rps", new SurveyRps()))
          setRpsDisplay({ isoStrain: 0, jobStrain: 0, actives: 0, passives: 0, relaxed: 0 })
        } else {
          store.dispatch(loadParticipation(props.dashboardFilters, false))

          const surveyRps = new SurveyRps({
            actives: data.countActive,
            passives: data.countPassive,
            relaxed: data.countRelaxed,
            isoStrain: data.countStressed,
            jobStrain: data.countJobStrain,
            total: data.countTotal
          })

          //Dispatché avant setIsLoading(false) : le skeleton doit disparaître une fois les
          //vraies valeurs déjà dans le store, sinon le useCountUpList anime vers les
          //anciennes valeurs (ou 0) pendant un tick avant de re-sauter vers les bonnes
          store.dispatch(surveyEdit("rps", surveyRps))
          setRpsDisplay({
            isoStrain: surveyRps.isoStrain,
            jobStrain: surveyRps.jobStrain,
            actives: surveyRps.actives,
            passives: surveyRps.passives,
            relaxed: surveyRps.relaxed
          })
        }

        setIsLoading(false)
      } else {
        setIsLoading(false)
      }
    }

    load()
  }, [props.dashboardFilters, props.survey.active.randomData])

  return props.survey.status === INSUFFICIENT_PARTICIPATION ? (
    <NoResultsWidget />
  ) : (
    <div>
      {currentHelp && <RpsHelp currentHelp={currentHelp} onClose={() => setCurrentHelp(null)} />}

      {currentTag && <QuestionSpecialHelp onClose={() => setCurrentTag(null)} />}

      <Card
        title={t("dashboard_overview")}
        helpSection="categories"
        exportName={t("dashboard_overview")}
        status={isCardLoading ? STATUS_LOADING : STATUS_LOADED}
        skeleton={<DashboardRpsOverviewSkeletonWidget />}>
        <div className="flex1 rps-container">
          <div className="flex flex-dcol">
            <div className="flex">
              <DashboardNumber
                color="#eb5a46"
                isInactive={rpsDisplay.isoStrain === 0}
                count={Math.round(animatedIsoStrain)}
                icon={faUserCircle}
                onInfo={() => openCategory("iso-strain")}
                header={t("rps")}
                title={t("rps_stressed")}
                tooltip={t("rps_help_text_3")}
                isReport={props.isReport}
              />

              <DashboardNumber
                color="#ff9500"
                isInactive={rpsDisplay.jobStrain === 0}
                count={Math.round(animatedJobStrain)}
                icon={faUserCircle}
                onInfo={() => openCategory("job-strain")}
                header={t("rps")}
                title={t("rps_job_strain")}
                tooltip={t("rps_help_text_4")}
                isReport={props.isReport}
              />

              <DashboardNumber
                isInactive={rpsDisplay.actives === 0}
                count={Math.round(animatedActives)}
                icon={faUserCircle}
                onInfo={() => openCategory("actives")}
                header={t("rps_monitor")}
                title={t("rps_active")}
                tooltip={t("rps_help_text_5")}
                isReport={props.isReport}
              />

              <DashboardNumber
                isInactive={rpsDisplay.passives === 0}
                count={Math.round(animatedPassives)}
                icon={faUserCircle}
                onInfo={() => openCategory("passives")}
                header={t("rps_monitor")}
                title={t("rps_passive")}
                tooltip={t("rps_help_text_6")}
                isReport={props.isReport}
              />

              <DashboardNumber
                isInactive
                count={Math.round(animatedRelaxed)}
                icon={faUserCircle}
                onInfo={() => openCategory("relaxed")}
                header={t("rps_ras")}
                title={t("rps_relaxed")}
                tooltip={t("rps_help_text_7")}
                isReport={props.isReport}
              />

              <DashboardNumber
                isInactive
                count={Math.round(animatedNc)}
                icon={faUserCircle}
                onInfo={() => openCategory("nc")}
                header={t("rps")}
                title={t("rps_nc")}
                tooltip={t("rps_help_text_8")}
                isReport={props.isReport}
              />
            </div>

            <p className="grey-t">{t("rps_sumary")}</p>
          </div>
        </div>
      </Card>
    </div>
  )
}

const mapStateToProps = (state) => ({
  survey: state.survey
})

export default connect(mapStateToProps)(withTranslation()(DashboardRpsOverviewWidget))
